/**
 * The ordering site's order button (`siteConfig.orderCta`).
 *
 * The Free plan promises "one order button to what you use today" — DM, phone,
 * a form, Square, Toast. When a seller sets one, the site stops being a cart
 * and becomes a menu with a button that leads there. Pure helpers so the site
 * page and the seller's settings agree on href, label and mode.
 *
 * Server-side validation/normalization lives in apps/api business/site-config.ts;
 * these helpers only read what it stored (and tolerate legacy/absent config).
 */

export type OrderCtaType = 'link' | 'phone' | 'email' | 'instagram' | 'none';

export interface OrderCta {
  type: OrderCtaType;
  value?: string;
  label?: string;
}

export type OrderingMode = 'sweetspot' | 'external' | 'pickup' | 'unavailable';

const TYPES: readonly OrderCtaType[] = ['link', 'phone', 'email', 'instagram', 'none'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INSTAGRAM_HANDLE_RE = /^[A-Za-z0-9._]{1,30}$/;

const DEFAULT_LABEL: Record<Exclude<OrderCtaType, 'none'>, string> = {
  link: 'Order online',
  phone: 'Call to order',
  email: 'Email to order',
  instagram: 'Order on Instagram',
};

/** Read `orderCta` off a siteConfig blob; anything malformed reads as absent. */
export function readOrderCta(config: Record<string, unknown> | null | undefined): OrderCta | null {
  const raw = config?.orderCta;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { type, value, label } = raw as Record<string, unknown>;
  if (typeof type !== 'string' || !TYPES.includes(type as OrderCtaType)) return null;
  const cta: OrderCta = { type: type as OrderCtaType };
  if (typeof value === 'string' && value.trim()) cta.value = value.trim();
  if (typeof label === 'string' && label.trim()) cta.label = label.trim();
  return cta;
}

/** Is this CTA one the site should send people to (vs. Sweetspot ordering)? */
export function orderCtaActive(cta: OrderCta | null | undefined): cta is OrderCta & { value: string } {
  return Boolean(cta && cta.type !== 'none' && cta.value);
}

/** The button's href, or null when there is nothing to link to. */
export function orderCtaHref(cta: OrderCta | null | undefined): string | null {
  if (!orderCtaActive(cta)) return null;
  switch (cta.type) {
    case 'link': {
      try {
        const url = new URL(cta.value);
        return url.protocol === 'https:' && !url.username && !url.password ? url.toString() : null;
      } catch {
        return null;
      }
    }
    case 'phone': {
      // tel: wants digits and a leading +; the display keeps the punctuation.
      const target = cta.value.replace(/[^0-9+]/g, '');
      const digits = target.replace(/\D/g, '');
      const plusCount = (target.match(/\+/g) ?? []).length;
      return digits.length >= 7 && digits.length <= 15 && plusCount <= 1 && (plusCount === 0 || target.startsWith('+'))
        ? `tel:${target}`
        : null;
    }
    case 'email': {
      const email = cta.value.toLowerCase();
      return email.length <= 254 && EMAIL_RE.test(email) ? `mailto:${email}` : null;
    }
    case 'instagram': {
      const handle = cta.value.replace(/^@+/, '');
      return INSTAGRAM_HANDLE_RE.test(handle) ? `https://www.instagram.com/${handle}/` : null;
    }
    default:
      return null;
  }
}

/** Button text: the seller's label when set, else a sensible default per type. */
export function orderCtaLabel(cta: OrderCta | null | undefined): string {
  if (!cta || cta.type === 'none') return 'Order';
  return cta.label?.trim() || DEFAULT_LABEL[cta.type];
}

/** Links that leave the site open in a new tab; tel:/mailto: hand off in place. */
export function orderCtaOpensNewTab(cta: OrderCta | null | undefined): boolean {
  return Boolean(cta && (cta.type === 'link' || cta.type === 'instagram'));
}

/**
 * How the site takes orders:
 *   external  → an orderCta with type ≠ none: no cart, the button leads elsewhere
 *   sweetspot → cart + card checkout (the seller accepts cards through Sweetspot)
 *   pickup    → cart + pay-at-pickup checkout (the existing fallback)
 *   unavailable → the plan has no Sweetspot ordering and no valid external CTA
 */
export function orderingMode(
  config: Record<string, unknown> | null | undefined,
  acceptsCards: boolean,
  orderingEnabled = true,
): OrderingMode {
  if (orderCtaHref(readOrderCta(config))) return 'external';
  if (!orderingEnabled) return 'unavailable';
  return acceptsCards ? 'sweetspot' : 'pickup';
}
